import type {
    ClientRequest,
    ClientResponse,
    CustomFields,
    ResourceIdentifier,
    Type,
    TypeDraft,
    TypePagedQueryResponse,
    TypeUpdate,
    TypeUpdateAction,
} from '@commercetools/platform-sdk';
import type { ByProjectKeyTypesRequestBuilder } from '@commercetools/platform-sdk/dist/declarations/src/generated/client/types/by-project-key-types-request-builder';
import { createSyncTypes } from '@commercetools/sync-actions';

import type { ApiClientDetails } from '../../types';
import { getQueryPredicate, QueryKey } from '../utils';
import { convertToComparableTypeDraft } from '../utils/sync-actions';

type QueryArgs = Parameters<ByProjectKeyTypesRequestBuilder['get']>[0];

// TODO: use expand objects
export function resolveCustomFields(custom: CustomFields): CustomFields {
    const { typeId, obj } = custom.type;
    const { fields } = custom;
    return <CustomFields>{
        type: <ResourceIdentifier>{
            typeId,
            key: obj && obj.key,
        },
        fields,
    };
}

export function typeApi({ apiRoot, ctpClient }: ApiClientDetails) {
    function typeClientRequest(args?: QueryArgs): ClientRequest {
        return apiRoot().types().get(args).clientRequest();
    }

    function fetchTypes(args?: QueryArgs): Promise<Type[]> {
        const clientRequest = typeClientRequest(args);

        return ctpClient().process(clientRequest, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
        });
    }

    function fetchAllTypes(expand?: string | string[]): Promise<Type[]> {
        return fetchTypes({ queryArgs: { expand } });
    }

    function getTypesByIds(ids: string[]): Promise<Type[]> {
        return fetchTypes({
            queryArgs: { where: getQueryPredicate(ids, QueryKey.ID) },
        });
    }

    async function getTypeById(id: string): Promise<Type> {
        return (await apiRoot().types().withId({ ID: id }).get().execute()).body;
    }

    function createType(ctType: TypeDraft): Promise<ClientResponse<Type>> {
        return apiRoot()
            .types()
            .post({
                body: ctType,
            })
            .execute();
    }

    function createTypes(types: TypeDraft[]): Promise<ClientResponse<Type>>[] {
        return types.map(createType);
    }

    async function getTypes(args?: QueryArgs): Promise<TypePagedQueryResponse> {
        const result = await apiRoot().types().get(args).execute();
        return result.body;
    }

    async function getTypeKeyById(id: string): Promise<string> {
        const response = await apiRoot().types().withId({ ID: id }).get().execute();

        return response.body.key;
    }

    async function getTypeByKey(key: string): Promise<Type> {
        return (await apiRoot().types().withKey({ key }).get().execute()).body;
    }

    async function updateTypeWithActions(
        key: string,
        version: number,
        actions: TypeUpdateAction[]
    ): Promise<ClientResponse<Type>> {
        return apiRoot().types().withKey({ key }).post({ body: { version, actions } }).execute();
    }

    async function updateType(next: TypeDraft): Promise<ClientResponse<Type>> {
        const syncTypes = createSyncTypes();
        const { key } = next;
        const previous = await getTypeByKey(key);
        const { version } = previous;

        let actions: TypeUpdateAction[] = <TypeUpdateAction[]>(
            syncTypes.buildActions(convertToComparableTypeDraft(next), previous)
        );

        // Workaround for the CT bug https://github.com/commercetools/nodejs/issues/1691
        actions = actions.filter(record => record.action !== 'changeFieldDefinitionOrder');

        return updateTypeWithActions(key, version, actions);
    }

    function updateTypeByAction(key: string, typeUpdate: TypeUpdate): Promise<ClientResponse<Type>> {
        return apiRoot().types().withKey({ key }).post({ body: typeUpdate }).execute();
    }

    async function removeTypeFieldDefinition(typeKey: string, fieldName: string) {
        const type = await getTypeByKey(typeKey);
        return apiRoot()
            .types()
            .withId({ ID: type.id })
            .post({
                body: {
                    version: type.version,
                    actions: [{ action: 'removeFieldDefinition', fieldName }],
                },
            })
            .execute();
    }

    async function removeType(typeKey: string) {
        const type = await getTypeByKey(typeKey);

        return apiRoot()
            .types()
            .withId({ ID: type.id })
            .delete({ queryArgs: { version: type.version } })
            .execute();
    }

    return {
        createType,
        createTypes,
        fetchAllTypes,
        getTypes,
        getTypeByKey,
        getTypeById,
        getTypesByIds,
        updateType,
        updateTypeWithActions,
        updateTypeByAction,
        getTypeKeyById,
        removeTypeFieldDefinition,
        removeType,
    };
}
